import type { ApiErrorEnvelope } from '@his/shared';
import { ApiError } from './api';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const TOKEN_KEY = 'his.public.token';
const CITY_KEY = 'his.public.city';

/**
 * The public directory's own client.
 *
 * Deliberately separate from `lib/api.ts`: the staff client sends a tenant
 * header and a staff token, and doing that here would either leak a staff
 * session into the public site or pin a patient to one hospital. The directory
 * has neither — one token, no tenant, and the hospital named in the path.
 */
export const publicSession = {
  token: (): string | null => localStorage.getItem(TOKEN_KEY),
  setToken: (token: string | null) =>
    token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY),
  /** Last city the visitor chose, so the search does not start from nothing. */
  city: (): string | null => localStorage.getItem(CITY_KEY),
  setCity: (city: string | null) =>
    city ? localStorage.setItem(CITY_KEY, city) : localStorage.removeItem(CITY_KEY),
  signOut: () => localStorage.removeItem(TOKEN_KEY),
};

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = publicSession.token();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE}/public${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  // A directory token lasts a week and has no refresh; when it finally expires
  // the only sane move is to forget it and let the visitor sign in again.
  if (response.status === 401 && token) {
    publicSession.signOut();
    window.dispatchEvent(new CustomEvent('his:public-signed-out'));
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'internal_error',
      envelope?.error?.message ?? response.statusText,
      envelope?.error?.details ?? [],
      envelope?.error?.requestId,
    );
  }
  return payload as T;
}

export const publicGet = <T>(path: string) => request<T>(path);
export const publicPost = <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body });
export const publicPut = <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body });
export const publicDelete = <T>(path: string) => request<T>(path, { method: 'DELETE' });
